import { get, post, put, request, refreshSession } from '../client';
import { session } from '../session';
import { announceSession } from '../sessionSync';
import type {
  LocationSummary, LoginRequest, LoginResponse, Me, MfaVerifyRequest, RoleKey, RoleSummary, SessionInfo, SessionUser, StepUpRequest, StepUpResponse, TokenResponse,
  UpdateContextRequest,
} from '../types/auth';
import type { Page, PurposeOfUse } from '../types/common';

/* ------------------------------------------------------------------------------------------------
 * Boundary normalisation. The UI codes against the `Me` / `SessionUser` types; core-api returns the
 * identity view (memberships + activeRoleId + providerResourceId). These adapters accept either shape.
 * ---------------------------------------------------------------------------------------------- */

type Raw = Record<string, unknown>;
const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/**
 * Backend role ids (services/core-api/src/platform/permissions.ts) → UI workspace, display name and the
 * default purpose of use that the backend policy permits for patient-scoped reads (platform/policy.ts).
 * Explicit map — no heuristics (R-108).
 */
export const BACKEND_ROLES: Record<string, { key: RoleKey; name: string; purpose: PurposeOfUse }> = {
  provider: { key: 'provider', name: 'Provider', purpose: 'TREATMENT' },
  nurse: { key: 'nurse', name: 'Nurse', purpose: 'TREATMENT' },
  medical_assistant: { key: 'nurse', name: 'Medical assistant', purpose: 'TREATMENT' },
  front_desk: { key: 'front_desk', name: 'Front desk', purpose: 'OPERATIONS' },
  access_center: { key: 'front_desk', name: 'Access center', purpose: 'OPERATIONS' },
  revenue_cycle: { key: 'revenue', name: 'Revenue cycle', purpose: 'PAYMENT' },
  him: { key: 'revenue', name: 'Health information management', purpose: 'OPERATIONS' },
  privacy: { key: 'privacy', name: 'Privacy officer', purpose: 'COMPLIANCE' },
  security: { key: 'privacy', name: 'Security officer', purpose: 'COMPLIANCE' },
  compliance: { key: 'privacy', name: 'Compliance', purpose: 'COMPLIANCE' },
  tenant_administrator: { key: 'tenant_admin', name: 'Tenant administrator', purpose: 'OPERATIONS' },
  platform_operator: { key: 'platform_operator', name: 'Platform operator', purpose: 'OPERATIONS' },
  // Integration role for inbound result import (no clinical workspace of its own)
  result_interface: { key: 'result_interface', name: 'Results interface', purpose: 'OPERATIONS' },
  ai_human_factors: { key: 'ai_governance', name: 'AI governance — human factors', purpose: 'OPERATIONS' },
  ai_data_science: { key: 'ai_governance', name: 'AI governance — data science', purpose: 'OPERATIONS' },
  ai_patient_representative: { key: 'ai_governance', name: 'AI governance — patient representative', purpose: 'OPERATIONS' },
  patient: { key: 'patient', name: 'Patient', purpose: 'PATIENT_REQUEST' },
  proxy: { key: 'proxy', name: 'Proxy', purpose: 'PATIENT_REQUEST' },
  // UI/mock role keys (mock mode) map to themselves
  revenue: { key: 'revenue', name: 'Revenue cycle / HIM', purpose: 'PAYMENT' },
  tenant_admin: { key: 'tenant_admin', name: 'Tenant administrator', purpose: 'OPERATIONS' },
};

export function roleKeyOf(v: unknown): RoleKey {
  return BACKEND_ROLES[String(v ?? '')]?.key ?? 'provider';
}

/** Default purpose of use for a backend role id (falls back to the workspace default). */
export function purposeForRole(roleId: string | undefined, fallback: PurposeOfUse = 'TREATMENT'): PurposeOfUse {
  return (roleId && BACKEND_ROLES[roleId]?.purpose) || fallback;
}

/**
 * UI permission intents → backend permission keys (R-039). The backend keys are the contract; UI code may
 * test either. `normalizeMe` adds an alias when ANY of its backend keys is granted.
 */
export const PERMISSION_ALIASES: Record<string, string[]> = {
  'note.write': ['note.create', 'note.update'],
  'problem.write': ['problem.manage'],
  'schedule.write': ['schedule.manage', 'appointment.manage', 'appointment.book'],
  'registration.write': ['patient.create', 'patient.update'],
  'rx.sign': ['prescription.sign'],
  'refill.decide': ['refill.manage'],
  'referral.write': ['referral.manage'],
  'order.write': ['order.create'],
  'order.pend': ['order.create'],
  'vitals.write': ['observation.manage'],
  'allergy.write': ['allergy.manage'],
  'medrec.write': ['medication.manage'],
  'admin.medication': ['administration.create'],
  'task.write': ['task.manage'],
  'message.reply': ['message.manage'],
  'document.write': ['document.manage'],
  'careteam.write': ['care_team.manage'],
  'billing.write': ['billing.manage'],
  'coding.query': ['billing.manage'],
  'payment.post': ['payment.create'],
  'admin.ai': ['ai.manage'],
  'disclosure.read': ['audit.read'],
  'breakglass.start': ['break_glass.start'],
  'breakglass.review': ['break_glass.review'],
  'legalhold.release': ['legal_hold.manage'],
  'chart.read': ['note.read'],
};

export function withAliases(perms: string[]): string[] {
  const set = new Set(perms);
  for (const [alias, targets] of Object.entries(PERMISSION_ALIASES)) if (targets.some((t) => set.has(t))) set.add(alias);
  return [...set];
}

const initials = (name: string) =>
  name
    .replace(/^(dr\.?|mr\.?|ms\.?|mrs\.?)\s+/i, '')
    .split(/[\s,]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0]!.toUpperCase())
    .join('') || '?';

export function normalizeUser(raw: unknown): SessionUser {
  const r = (raw ?? {}) as Raw;
  const displayName = str(r.displayName) ?? str(r.display_name) ?? str(r.username) ?? 'Unknown user';
  const audience = str(r.audience);
  const kind = (str(r.kind) as SessionUser['kind'] | undefined) ?? (audience === 'patient' ? 'patient' : r.platformOperator ? 'platform' : 'workforce');
  return {
    id: String(r.id ?? ''),
    username: str(r.username) ?? '',
    displayName,
    initials: str(r.initials) ?? initials(displayName),
    kind,
    // Only an explicit active tenant counts — never "the first membership" (R-110).
    tenantId: (r.tenantId as string | null | undefined) ?? str(r.activeTenantId) ?? null,
  };
}

export function normalizeMe(raw: unknown): Me {
  const r = (raw ?? {}) as Raw;
  if (r.context && r.user) {
    const m = r as unknown as Me;
    return { ...m, permissions: withAliases(m.permissions) };
  }
  const user = normalizeUser(r);
  const memberships = Array.isArray(r.memberships) ? (r.memberships as Raw[]) : [];
  const tenantId = str(r.activeTenantId) ?? null;
  const inTenant = memberships.filter((m) => !tenantId || str(m.tenantId) === tenantId);
  const roles: RoleSummary[] = [];
  for (const m of inTenant) {
    const id = str(m.roleId) ?? str(m.role) ?? '';
    if (!id || roles.some((x) => x.id === id)) continue;
    roles.push({ id, key: roleKeyOf(id), name: str(m.roleName) ?? BACKEND_ROLES[id]?.name ?? id });
  }
  if (r.platformOperator && !roles.length) roles.push({ id: 'platform_operator', key: 'platform_operator', name: 'Platform operator' });
  if (user.kind === 'patient' && !roles.length) roles.push({ id: 'patient', key: 'patient', name: 'Patient' });
  const locations: LocationSummary[] = [];
  const add = (id: string | undefined, name?: string, facility?: string, department?: string) => {
    if (id && !locations.some((x) => x.id === id)) locations.push({ id, name: name ?? 'Location', facility: facility ?? '', department: department ?? '' });
  };
  if (Array.isArray(r.locations)) for (const l of r.locations as Raw[]) add(str(l.id) ?? str(l.locationId), str(l.name) ?? str(l.locationName), str(l.facility) ?? str(l.facilityName), str(l.department) ?? str(l.departmentName));
  for (const m of inTenant) add(str(m.locationId), str(m.locationName), str(m.facilityName), str(m.departmentName));
  const activeRoleId = str(r.activeRoleId) ?? roles[0]?.id ?? '';
  const role = roles.find((x) => x.id === activeRoleId) ?? { id: activeRoleId, key: roleKeyOf(activeRoleId), name: BACKEND_ROLES[activeRoleId]?.name ?? activeRoleId };
  const locationId = (r.locationId as string | null | undefined) ?? null;
  const tenantMember = inTenant[0];
  return {
    user: { ...user, tenantId },
    tenant: tenantId ? { id: tenantId, slug: str(tenantMember?.tenantSlug) ?? '', name: str(tenantMember?.tenantName) ?? 'Organization' } : null,
    roles,
    locations,
    context: { roleId: role.id, role, locationId, location: locations.find((l) => l.id === locationId) ?? null },
    permissions: withAliases(Array.isArray(r.permissions) ? (r.permissions as string[]) : []),
    providerResourceId: str(r.providerResourceId) ?? null,
    session: (r.session as Me['session'] | undefined) ?? { idleTimeoutSeconds: 900, absoluteExpiresAt: new Date(Date.now() + 12 * 3600_000).toISOString() },
  };
}

function normalizeTokens(raw: unknown): TokenResponse {
  const r = (raw ?? {}) as Raw;
  return { accessToken: String(r.accessToken), expiresIn: Number(r.expiresIn ?? 900), user: normalizeUser(r.user) };
}

function normalizeSession(raw: unknown, currentId?: string): SessionInfo {
  const r = (raw ?? {}) as Raw;
  return {
    id: String(r.id ?? ''),
    createdAt: String(r.createdAt ?? ''),
    lastSeenAt: String(r.lastSeenAt ?? r.createdAt ?? ''),
    device: str(r.device) ?? (str(r.audience) ? `${r.audience} session` : 'Unknown device'),
    ip: str(r.ip) ?? '—',
    current: typeof r.current === 'boolean' ? r.current : r.id === currentId,
  };
}

export const authApi = {
  async login(body: LoginRequest): Promise<LoginResponse> {
    const r = await post<Raw>('/auth/login', body, { auth: false });
    if (r.status === 'mfa_required') return { status: 'mfa_required', mfaToken: String(r.mfaToken) };
    const t = normalizeTokens(r);
    session.setTokens(t.accessToken, t.expiresIn, t.user);
    announceSession('signin', t.user);
    return { status: 'authenticated', ...t };
  },
  async verifyMfa(body: MfaVerifyRequest): Promise<TokenResponse> {
    const t = normalizeTokens(await post<Raw>('/auth/mfa/verify', body, { auth: false }));
    session.setTokens(t.accessToken, t.expiresIn, t.user);
    announceSession('signin', t.user);
    return t;
  },
  /** Re-verify identity for a high-risk action. Stores the token for X-Step-Up-Token. */
  async stepUp(body: StepUpRequest): Promise<StepUpResponse> {
    const r = await post<StepUpResponse>('/auth/step-up', body);
    session.setStepUp(r.stepUpToken, r.expiresIn);
    return r;
  },
  refresh: refreshSession,
  async lock(): Promise<void> {
    const { user, accessToken } = session.get();
    // Send revocation with the current token without refreshing it. Clear immediately, never after
    // the response: a slow revocation must not erase a subsequent same-user sign-in.
    const revocation = request('POST', '/auth/logout', { auth: false, headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {} });
    session.clear('expired');
    announceSession('lock', user);
    await revocation;
  },
  async logout(): Promise<void> {
    try {
      await request('POST', '/auth/logout');
    } finally {
      session.clear('logout');
      announceSession('signout');
    }
  },
  me: async () => {
    const me = normalizeMe(await get<unknown>('/me'));
    session.setUser(me.user);
    return me;
  },
  /** PUT /me/context returns only {roleId, tenantId, locationId}; re-read /me for the full view (R-040). */
  updateContext: async (body: UpdateContextRequest) => {
    const r = await put<Raw>('/me/context', body);
    if (r && (r as Raw).context && (r as Raw).user) return normalizeMe(r);
    if (str(r?.tenantId)) session.setContext({ tenantId: String(r.tenantId) });
    return normalizeMe(await get<unknown>('/me'));
  },
  sessions: async (): Promise<Page<SessionInfo>> => {
    const r = await get<{ items: unknown[]; nextCursor: string | null }>('/me/sessions');
    const me = session.get();
    const tokenSid = (() => {
      try {
        return JSON.parse(atob((me.accessToken ?? '').split('.')[1] ?? '')).sid as string | undefined;
      } catch {
        return undefined;
      }
    })();
    return { items: (r.items ?? []).filter((x) => !(x as Raw).revokedAt).map((x) => normalizeSession(x, tokenSid)), nextCursor: r.nextCursor ?? null };
  },
  revokeSession: (id: string) => post<void>(`/me/sessions/${encodeURIComponent(id)}:revoke`, {}, { idempotent: true }),
};
